
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Search,
  ListChecks,
  HelpCircle,
  Settings,
  PanelLeftClose,
  PanelRightOpen,
  BarChart3,
  Target,
  MessageSquare,
  Sparkles,
  Layers,
} from 'lucide-react';
import { useIsMobile, useBreakpoint } from '@/hooks/use-mobile';
import { useSidebarContext } from '@/contexts/SidebarContext';
import ThemeToggle from './ThemeToggle';
import { cn } from '@/lib/utils';
import { logComponentError } from '@/utils/logger';
import { clearAIReadinessReferences } from '@/utils/cache/refreshUtils';

interface AppSidebarProps {
  activePage?: string;
  onNavigate?: (page: string) => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ 
  activePage = "dashboard", 
  onNavigate 
}) => {
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  const { compact, setCompact } = useSidebarContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-manage sidebar state based on screen size
  React.useEffect(() => {
    if (breakpoint === 'mobile') {
      setCompact(true);
    } else if (breakpoint === 'large') {
      setCompact(false);
    }
  }, [breakpoint, setCompact]);
  
  // Navbar items definition with enhanced icons and descriptions
  const navItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: <BarChart3 className="h-4 w-4" />, 
      description: 'Website analysis overview'
    },
    { 
      id: 'deep-analysis', 
      label: 'Deep Content Analysis', 
      icon: <Target className="h-4 w-4" />, 
      description: 'Detailed page insights'
    },
    { 
      id: 'recommended', 
      label: 'Related Articles', 
      icon: <Sparkles className="h-4 w-4" />, 
      description: 'AI content suggestions'
    },
    { 
      id: 'faq-serp', 
      label: 'FAQ Analysis', 
      icon: <MessageSquare className="h-4 w-4" />, 
      description: 'FAQ optimization tool'
    },
    { 
      id: 'fact-check', 
      label: 'Fact-Checking', 
      icon: <ListChecks className="h-4 w-4" />, 
      description: 'Verify facts using AI'
    },
    { 
      id: 'reddit-hn', 
      label: 'Reddit–HN Coverage', 
      icon: <MessageSquare className="h-4 w-4" />, 
      description: 'Reddit & Hacker News analysis'
    },
    { 
      id: 'startup-stash', 
      label: 'Startup Stash Listicle', 
      icon: <Layers className="h-4 w-4" />, 
      description: 'TOC automation for listicles'
    },
    { 
      id: 'grow-geo', 
      label: 'Grow GEO', 
      icon: <Target className="h-4 w-4" />, 
      description: 'LLM response website analysis'
    },
  ];

  const ecoNavItem = {
    id: 'eco',
    label: 'ECO Assistant',
    path: '/eco',
    icon: <Sparkles className="h-4 w-4" />,
    description: 'Enhanced Content Optimizer'
  };
  
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
      if (page === 'fact-check') {
        navigate('/fact-check');
      }
      
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(page);
        if (element) {
          try {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } catch (error) {
            logComponentError('AppSidebar', error as Error, { action: 'scroll to section', page });
          }
        }
      }, 100);
    }
  };


  // Navigate to dashboard section
  const navigateToDashboard = () => {
    console.log("AppSidebar: Navigating to dashboard section");
    if (onNavigate) {
      handleNavigate("dashboard");
    }
  };

  
  // Force a refresh to clear any cached sidebar state
  React.useEffect(() => {
    // Call the clearAIReadinessReferences function
    clearAIReadinessReferences();
    
    // Additional cleanup specific to this component
    try {
      // Only remove AI Readiness navigation menu items, not content elements
      const aiReadinessNavSelectors = [
        'nav button[aria-label*="AI Readiness"]',
        'nav a[aria-label*="AI Readiness"]',
        '.sidebar .ai-readiness-nav-item',
        '.sidebar [data-page="ai-readiness"]',
        '.sidebar [data-section="ai-readiness"]'
      ];
      
      aiReadinessNavSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          console.log('Found and removed AI readiness navigation element:', el);
          el.remove();
        });
      });
      
      // Only search for AI readiness text within navigation/sidebar areas
      document.querySelectorAll('.sidebar nav button, .sidebar nav a, nav button, nav a').forEach(el => {
        if (el.textContent && el.textContent.toLowerCase().includes('ai readiness')) {
          console.log('Found and removed AI readiness navigation text:', el);
          el.remove();
        }
      });
    } catch (e) {
      console.error('Error removing AI Readiness sidebar items:', e);
    }
  }, []);
  
  const sidebarWidth = compact ? "w-16 min-w-16" : "w-64 min-w-64";
  const sidebarClasses = cn(
    // Base styles
    "flex flex-col bg-gradient-to-b from-background to-muted/20 border-r border-border/60 transition-all duration-300",
    // Width management
    sidebarWidth,
    // Responsive positioning and height
    {
      // Mobile: Overlay sidebar
      "fixed top-0 left-0 z-50 h-screen": isMobile,
      // Desktop: Fixed sidebar with header offset
      "fixed top-20 left-0 z-40 h-[calc(100vh-5rem)]": !isMobile,
    }
  );

  return (
    <aside className={sidebarClasses}>
      <div className="flex-1 space-y-6 py-6 overflow-y-auto">
        {/* Brand section */}
        <div className="px-4">
          <div className={cn("flex items-center gap-3 transition-all duration-300", compact && "justify-center")}>
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-purple-400 rounded-lg flex items-center justify-center">
              <span className="text-white text-sm font-bold">DC</span>
            </div>
            {!compact && (
              <div className="flex flex-col">
                <span className="font-semibold text-sm">DeepCrawl</span>
                <span className="text-xs text-muted-foreground">Analytics</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation items */}
        <div className="space-y-1 px-3">
          {navItems.map(item => (
            <div key={item.id} className="group">
                <button 
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200",
                  // Enhanced touch targets for mobile
                  isMobile ? "min-h-12 touch-manipulation" : "min-h-10",
                  activePage === item.id 
                    ? "bg-primary/10 text-primary border border-primary/20 shadow-sm" 
                    : "hover:bg-accent/50 hover:text-accent-foreground"
                )}
                onClick={() => {
                  if (item.id === 'dashboard') {
                    navigateToDashboard();
                  } else if (item.id === 'fact-check' || item.id === 'reddit-hn' || item.id === 'startup-stash' || item.id === 'grow-geo') {
                    navigate(`/${item.id}`);
                  } else {
                    handleNavigate(item.id);
                  }
                }}
              >
                <span className={cn("transition-transform duration-200 shrink-0", activePage === item.id && "scale-110")}>
                  {item.icon}
                </span>
                {!compact && (
                  <div className="flex flex-col items-start flex-1 min-w-0">
                    <span className="truncate w-full">{item.label}</span>
                    <span className="text-xs text-muted-foreground group-hover:text-muted-foreground/80 transition-colors truncate w-full">
                      {item.description}
                    </span>
                  </div>
                )}
              </button>
            </div>
          ))}
          
          {/* ECO Assistant Navigation */}
          <div className="group">
            <button 
              className={cn(
                "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200",
                // Enhanced touch targets for mobile
                isMobile ? "min-h-12 touch-manipulation" : "min-h-10",
                location.pathname === ecoNavItem.path 
                  ? "bg-gradient-to-r from-purple-600/10 to-purple-500/10 text-purple-600 border border-purple-500/20 shadow-sm" 
                  : "hover:bg-accent/50 hover:text-accent-foreground"
              )}
              onClick={() => navigate(ecoNavItem.path)}
            >
              <span className={cn("transition-transform duration-200 shrink-0", location.pathname === ecoNavItem.path && "scale-110")}>
                {ecoNavItem.icon}
              </span>
              {!compact && (
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <span className="truncate w-full">{ecoNavItem.label}</span>
                  <span className="text-xs text-muted-foreground group-hover:text-muted-foreground/80 transition-colors truncate w-full">
                    {ecoNavItem.description}
                  </span>
                </div>
              )}
            </button>
          </div>
        </div>
        
      </div>
      
      <div className="mt-auto border-t px-2 pt-2">
        <button 
          onClick={() => setCompact(!compact)}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent"
        >
          {compact ? (
            <>
              <PanelRightOpen className="h-4 w-4" />
              {!compact && <span>Expand</span>}
            </>
          ) : (
            <>
              <PanelLeftClose className="h-4 w-4" />
              {!compact && <span>Collapse</span>}
            </>
          )}
        </button>
        
        <ThemeToggle />
      </div>
    </aside>
  );
};

export default AppSidebar;
